import React, { useRef } from 'react';
import './SearchImportBar.css';

export interface SearchImportBarProps {
  onImport?: (file: File) => void | Promise<void>;
  onImportFiles?: (files: File[]) => void | Promise<void>;
  disabled?: boolean;
  loading?: boolean;
  accept?: string;
  title?: string;
  className?: string;
  multiple?: boolean;
  directory?: boolean;
}

function matchesAccept(file: File, accept: string): boolean {
  const filters = accept.split(',').map((part) => part.trim().toLowerCase()).filter(Boolean);
  if (filters.length === 0) {
    return true;
  }
  const name = file.name.toLowerCase();
  const type = (file.type || '').toLowerCase();
  return filters.some((filter) => {
    if (filter.startsWith('.')) {
      return name.endsWith(filter);
    }
    return type === filter;
  });
}

const SearchImportBar: React.FC<SearchImportBarProps> = ({
  onImport,
  onImportFiles,
  disabled = false,
  loading = false,
  accept = '.csv',
  title = 'Import contacts from CSV',
  className = '',
  multiple = false,
  directory = false
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    if (disabled || loading) return;
    inputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    e.target.value = '';
    const files = selected.filter((file) => matchesAccept(file, accept));
    if (files.length === 0) {
      return;
    }
    if (onImportFiles) {
      await onImportFiles(files);
      return;
    }
    if (onImport) {
      await onImport(files[0]);
    }
  };

  const directoryAttrs = directory
    ? ({ webkitdirectory: '', directory: '' } as React.InputHTMLAttributes<HTMLInputElement>)
    : {};

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple || directory}
        onChange={handleFileChange}
        className="search-import-bar-input-hidden"
        aria-hidden="true"
        {...directoryAttrs}
      />
      <button
        type="button"
        className={`search-import-bar-button ${className}`.trim()}
        onClick={handleClick}
        disabled={disabled || loading}
        title={title}
        aria-label={title}
      >
        {loading ? (
          <span className="search-import-bar-spinner" aria-hidden="true">⋯</span>
        ) : (
          <svg
            className="search-import-bar-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
        )}
      </button>
    </>
  );
};

export default SearchImportBar;
